import { FileWithDuration, useFileMetadataExtraction } from '@lib/hooks/useFileMetadataExtraction';
import { useOwnerAffordability } from '@lib/hooks/useOwnerAffordability';
import { estimateIngest } from '@lib/utils/ingestEstimate';
import { computeBillingImpact } from '@stores/Billing';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { notificationStore } from '@stores/Notifications';
import { trackingStore } from '@stores/Tracking';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { useCallback, useEffect, useMemo, useState } from 'react';

// NOTE: This hook covers the multi-step file upload flow (select → confirm → upload).
// The single-media URL upload case is handled separately by UrlImportComponent
// (src/components/MediaUploadBox/UrlImportComponent.tsx), which has its own
// credit/storage checks but is also part of the upload flow.
//
// ── Flow state ──────────────────────────────────────────────────────────
//
//  idle ──open()──▶ pending
//                      │
//       ┌──────────────┼──────────────┐
//       ▼              ▼              ▼
//   selecting      confirming     (duplicate toast + idle)
//   (multi-file)   (single-file)
//       │
//       ▼
//   confirming
//       │
//       ▼
//     idle (after confirm/cancel)

export type FileUploadFlowState = 'idle' | 'pending' | 'selecting' | 'confirming';

interface UseFileUploadFlowOptions {
  files: File[];
  onConfirm?: (selectedFiles: FileWithDuration[], libraryName?: string) => Promise<void>;
  onDismiss?: () => void;
  createNewLibrary?: boolean;
  /** Name for the new library when createNewLibrary — entered on the upload screen,
   *  so the confirm modal shows it read-only instead of editing it. */
  newLibraryName?: string;
}

export function useFileUploadFlow({
  files,
  onConfirm,
  onDismiss,
  createNewLibrary = false,
  newLibraryName,
}: UseFileUploadFlowOptions) {
  const [flowState, setFlowState] = useState<FileUploadFlowState>('idle');
  const [selectedIndexes, setSelectedIndexes] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // ── Metadata extraction ─────────────────────────────────────────────
  const { filesWithMetadata, filesWithFingerprints, isFingerprintingComplete } =
    useFileMetadataExtraction(files);

  // ── Duplicate detection ─────────────────────────────────────────────
  const libraryFolder = useLibraryFolder();
  const uploadManager = useUploadManager();

  const activeUploadFingerprints = Object.entries(uploadManager.uploadStatus)
    .filter(
      ([, entry]) => entry.fingerprint && entry.status !== 'error' && entry.status !== 'cancelled',
    )
    .map(([uuid, entry]) => `${entry.fingerprint}:${uuid}`)
    .join(',');

  // Key the memo on the CONTENT of libraryFolder.uniqueIds, not its identity:
  // the computed mints a new (usually content-identical) Record on every
  // folder poll tick while media is processing, which would re-mint the merged
  // record each tick and cascade into every consumer memo (selection dedup,
  // FileUploadDataGrid, ChooseFileUploadModal). Unlike the URL flow this must
  // NOT be a frozen snapshot — the dedup effect below deliberately deselects
  // live when library contents genuinely change.
  const libraryUniqueIdsSignature = Object.entries(libraryFolder.uniqueIds)
    .map(([uniqueId, mediaId]) => `${uniqueId}:${mediaId}`)
    .sort()
    .join(',');

  const existingUniqueIds = useMemo(() => {
    // A brand-new library has no existing media (and in-flight uploads target other
    // libraries), so nothing counts as a duplicate. Dedup retargets automatically
    // for existing libraries because libraryFolder is re-provided per target.
    if (createNewLibrary) return {} as Record<string, string>;
    const ids: Record<string, string> = { ...libraryFolder.uniqueIds };
    for (const [uuid, entry] of Object.entries(uploadManager.uploadStatus)) {
      if (entry.fingerprint && entry.status !== 'error' && entry.status !== 'cancelled') {
        ids[entry.fingerprint] = uuid;
      }
    }
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryUniqueIdsSignature, activeUploadFingerprints, createNewLibrary]);

  // ── Route pending → selecting or confirming ─────────────────────────
  useEffect(() => {
    if (flowState !== 'pending') return;
    if (filesWithMetadata.length === 0) return;

    // Multi-file: open the selection grid IMMEDIATELY — don't wait for
    // thumbnail/duration extraction or fingerprinting (the slow part with many
    // files). Select everything by default; duplicates are removed from the
    // selection asynchronously as fingerprints arrive (the dedup effect below),
    // and the grid shows skeletons until each file's metadata lands.
    if (filesWithMetadata.length > 1) {
      setSelectedIndexes(filesWithMetadata.map((_, index) => index.toString()));
      setFlowState('selecting');
      return;
    }

    // Single file: its metadata is cheap, so wait for it to keep the
    // all-duplicate card pre-check and an accurate confirm summary.
    if (!isFingerprintingComplete) return;

    // Compute non-duplicate indexes
    const nonDuplicateIndexes = filesWithMetadata
      .map((file, index) => {
        const fingerprint = filesWithFingerprints.get(file);
        const isDuplicate = fingerprint && fingerprint in existingUniqueIds;
        return isDuplicate ? null : index.toString();
      })
      .filter((index): index is string => index !== null);

    setSelectedIndexes(nonDuplicateIndexes);

    // All duplicates → rich event card, no fleeting toast. Card uses the
    // BatchCard-style layout (filename + "+ N others" + library subtitle)
    // so it reads as a sibling of the upload BatchCard rather than a
    // separate visual family.
    if (nonDuplicateIndexes.length === 0) {
      trackingStore.fileUploadAllDuplicates({ fileCount: filesWithMetadata.length });
      const libId = libraryFolder.library.libraryId;
      const libName = libraryFolder.library.data?.name ?? 'library';
      notificationStore.pushRichEvent({
        type: 'rich-event',
        variant: 'warning',
        iconKind: 'duplicate',
        // Title-leading filename is rendered from rows[0] by the card when
        // a subtitle is present. The `header` is kept as a no-op fallback
        // for surfaces that don't honour the BatchCard-style layout.
        header: '',
        rows: filesWithMetadata.map((f, i) => {
          const fingerprint = filesWithFingerprints.get(f);
          const matchedMediaId = fingerprint ? existingUniqueIds[fingerprint] : undefined;
          // Use the local file's pre-extracted thumbnail / LQIP if it's a
          // media file — same path the upload cards use.
          const enriched = f as FileWithDuration;
          const thumbnail = enriched.thumbnail ?? enriched.lqip ?? null;
          return {
            key: `${f.name}-${i}`,
            filename: f.name,
            reason: 'Already in this library',
            thumbnailUrl: thumbnail,
            viewInLibrary: matchedMediaId
              ? { libraryId: libId, mediaId: matchedMediaId, libraryName: libName }
              : undefined,
          };
        }),
        subtitle: {
          libraryName: libName,
          libraryUrl: `/library/${libId}`,
          statusText: 'Already in library',
          statusVariant: 'warning',
        },
        modalTitle: 'Duplicate files',
      });
      trackingStore.uploadRichEventShown({
        kind: 'duplicate',
        rowCount: filesWithMetadata.length,
      });
      setFlowState('idle');
      onDismiss?.();
      return;
    }

    // Single, non-duplicate file → straight to confirming.
    setFlowState('confirming');
  }, [
    flowState,
    isFingerprintingComplete,
    filesWithMetadata,
    filesWithFingerprints,
    existingUniqueIds,
    onDismiss,
  ]);

  // While the grid is open, remove duplicates from the selection as their
  // fingerprints arrive (per batch, and when library contents change). Only
  // ever DESELECTS — never re-adds — so the user's manual (de)selections during
  // loading are preserved.
  useEffect(() => {
    if (flowState !== 'selecting') return;
    setSelectedIndexes((prev) =>
      prev.filter((index) => {
        const file = filesWithMetadata[parseInt(index)];
        if (!file) return false;
        const fingerprint = filesWithFingerprints.get(file);
        return !(fingerprint && fingerprint in existingUniqueIds);
      }),
    );
  }, [flowState, filesWithMetadata, filesWithFingerprints, existingUniqueIds]);

  // ── Derived data ────────────────────────────────────────────────────
  const { selectedFiles, ingestEstimate } = useMemo(() => {
    const selected = selectedIndexes
      .map((index) => filesWithMetadata[parseInt(index)])
      .filter(Boolean);
    return { selectedFiles: selected, ingestEstimate: estimateIngest(selected) };
  }, [selectedIndexes, filesWithMetadata]);

  const { creditCost, creditsLeft, creditsAfterProcessing, storageLeft, storageAfterProcessing } =
    computeBillingImpact(ingestEstimate);

  // Whose credits this upload actually spends. `computeBillingImpact` always reports the current
  // user's balance, but the backend charges the library's `billingId` — so when they differ, every
  // figure above belongs to the wrong account and must not gate anything (see
  // `resolveBillingOwnership`).
  //
  // Keyed on the billing id and not on who owns the library: a teammate's library on a shared
  // org billing account is somebody else's and still spends this user's balance, so the gates
  // below have to keep applying to it.
  //
  // Suppressed for `createNewLibrary`: the library doesn't exist yet, so there is nothing to
  // compare against and `libraryFolder` still points at whatever library the box was mounted
  // under. A library the user is about to create is billed to the user.
  const billedToAnotherAccount =
    !createNewLibrary && (libraryFolder.library?.isBilledToAnotherAccount ?? false);

  // Who to name in the notice, from the *ownership* axis — the only one that carries a person.
  // Gated on the flag so it inherits the `createNewLibrary` carve-out above: `otherOwnerName`
  // answers for the library this box is mounted under, which says nothing about a library that
  // doesn't exist yet.
  //
  // Stays a value of its own rather than folding into the flag, because the two can legitimately
  // disagree: an owner the backend gave no name and no email, or a personal library billed
  // elsewhere, leaves this undefined while the flag is still true. "Somebody else pays" is the
  // part the user must not miss, and the notice has a nameless wording for exactly that.
  const ownerName = billedToAnotherAccount ? libraryFolder.library?.otherOwnerName : undefined;

  // One probe for the whole flow, shared by the selection grid and the confirm screen. Priced on
  // the non-duplicate selection: the dedup effect above drops duplicates from `selectedIndexes`
  // before this reads it, matching what the backend bills.
  const ownerAffordability = useOwnerAffordability({
    libraryId: libraryFolder.library?.libraryId,
    ingestSeconds: ingestEstimate.durationSeconds,
    enabled: billedToAnotherAccount,
  });

  // ── Actions ─────────────────────────────────────────────────────────
  // Stable reference — safe to use in useCallback deps without causing re-registration
  const open = useCallback(() => {
    setFlowState('pending');
  }, []);

  const cancelSelection = () => {
    setFlowState('idle');
    setSelectedIndexes([]);
    onDismiss?.();
  };

  const advanceToConfirm = () => {
    setFlowState('confirming');
  };

  const cancelConfirm = () => {
    // Guard: ignore spurious calls (e.g. BaseModal close on mount)
    if (flowState !== 'confirming') return;

    if (files.length <= 1) {
      // Single file (or no files): go back to idle
      setFlowState('idle');
      onDismiss?.();
    } else {
      // Multi file: go back to selection grid
      setFlowState('selecting');
    }
  };

  const confirmUpload = async (libraryName?: string) => {
    // Attach pre-computed fingerprints
    for (const file of selectedFiles) {
      const fp = filesWithFingerprints.get(file);
      if (fp) file.fingerprint = fp;
    }

    if (onConfirm) {
      try {
        setIsUploading(true);
        // The name now comes from the upload screen (newLibraryName); the confirm
        // modal no longer supplies one, so fall back to it.
        await onConfirm(selectedFiles, libraryName ?? newLibraryName);
        setFlowState('idle');
        setSelectedIndexes([]);
      } catch (error) {
        console.error('Error uploading files:', error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  return {
    flowState,
    open,

    // Selection grid (multi-file)
    selectionProps: {
      filesWithMetadata,
      filesWithFingerprints,
      existingUniqueIds,
      selectedIndexes,
      onSelectionChange: setSelectedIndexes,
      onCancel: cancelSelection,
      onAdvanceToConfirm: advanceToConfirm,
      creditCost,
      creditsLeft,
      creditsAfterProcessing,
      storageLeft,
      storageAfterProcessing,
      ingestEstimate,
      isUploading,
      isFingerprintingComplete,
      billedToAnotherAccount,
      ownerName,
      ownerAffordability,
    },

    // Summary confirm (both flows)
    summaryProps: {
      open: flowState === 'confirming',
      onClose: cancelConfirm,
      onConfirm: confirmUpload,
      selectedFiles,
      isLoading: isUploading,
      createNewLibrary,
      newLibraryName,
      creditCost,
      storageAfterProcessing,
      ingestEstimate,
      billedToAnotherAccount,
      ownerName,
      ownerAffordability,
    },
  };
}
